import {
  KeyboardEventHandler,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import {
  Box,
  ClickAwayListener,
  Grow,
  InputAdornment,
  Paper,
  Popper,
  PopperProps,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { KeyboardHints } from "~/components/KeyboardHints/KeyboardHints";

export type FilterMenuDropdownProps = {
  children: ReactNode;
  open: boolean;
  onClose: () => void;
  anchorEl: PopperProps["anchorEl"];
  onSearchInputChange?: TextFieldProps["onChange"];
  placeholder?: string;
  showKeyboardHint?: boolean;
  onDropdownSearchFieldKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  startAdornment?: ReactElement;
  enableSearch?: boolean;
  placement?: PopperProps["placement"];
};

export function FilterMenuDropdown({
  children,
  onClose,
  open,
  anchorEl,
  onSearchInputChange,
  showKeyboardHint,
  placeholder,
  onDropdownSearchFieldKeyUp,
  startAdornment,
  enableSearch = false,
  placement = "bottom-start",
}: FilterMenuDropdownProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleEscapePress = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleEscapePress);

    return () => {
      window.removeEventListener("keydown", handleEscapePress);
    };
  }, []);

  return (
    <Popper
      sx={{
        zIndex: 2,
      }}
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      transition
      disablePortal
      modifiers={[{ name: "flip", enabled: false }]}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
          <Paper
            sx={{
              border: "1px solid",
              borderColor: "primary.main",
              borderTopLeftRadius: 0,
              maxWidth: "400px",
              mt: "-1px",
              backgroundColor: "background.default",
              "& .MuiPopper-root": {
                zIndex: 100,
              },
            }}
          >
            <ClickAwayListener onClickAway={onClose}>
              <Box
                ref={containerRef}
                tabIndex={-1}
                sx={{ minWidth: "100px", zIndex: 2 }}
              >
                {enableSearch && (
                  <TextField
                    data-testid="values-search-input"
                    autoComplete="off"
                    color="primary"
                    placeholder={placeholder}
                    size="small"
                    onChange={onSearchInputChange}
                    onKeyUp={onDropdownSearchFieldKeyUp}
                    sx={{
                      width: "100%",
                      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderWidth: "1px !important",
                      },
                    }}
                    slotProps={{
                      input: {
                        sx: {
                          bgcolor: "code.background",
                          m: 2,
                          mb: 0,
                          px: 1,
                        },
                        startAdornment: startAdornment || (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: "24px" }} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}

                {children}

                {showKeyboardHint && <KeyboardHints />}
              </Box>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
}
